/**
 * App.js — Week 3+
 *
 * /admin routing logic:
 *   - User in `systemAdmins` collection → SystemAdminDashboard (AdminDashboard)
 *   - User with role owner/admin in their tenant → CompanyAdminDashboard
 *   - Anyone else → redirected to /company
 */
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebaseConfig";
import { AuthProvider, useAuth }   from "./contexts/AuthContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import ProtectedRoute               from "./components/auth/ProtectedRoute";
import LoginPage                    from "./pages/LoginPage";
import RegisterPage                 from "./pages/RegisterPage";
import AdminDashboard               from "./pages/admin/AdminDashboard";          // system admin
import CompanyAdminDashboard        from "./pages/admin/CompanyAdminDashboard";   // company admin
import Dashboard                    from "./Dashboard";
import CompanyDashboard             from "./pages/CompanyDashboard";
import CampaignsPage                from "./pages/campaigns/CampaignsPage";
import CampaignDetailPage           from "./pages/campaigns/CampaignDetailPage";
import PublicSharePage              from "./pages/PublicSharePage";
import ProfilePage                  from "./pages/ProfilePage";

/* ── Redirect logged-in users away from login/register ──── */
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
      <div className="text-blue-400 text-xl">Loading…</div>
    </div>
  );
  if (user) return <Navigate to="/company" replace />;
  return children;
};

/* ── Smart Admin Route ───────────────────────────────────────
   Checks Firestore systemAdmins collection.
   - System admin  → AdminDashboard
   - Owner/Admin   → CompanyAdminDashboard
   - Anyone else   → /company
─────────────────────────────────────────────────────────── */
const AdminRoute = () => {
  const { user, userData, loading: authLoading } = useAuth();
  const [isSystemAdmin, setIsSystemAdmin] = useState(null); // null = checking

  useEffect(() => {
    if (!user?.uid) return;
    getDoc(doc(db, "systemAdmins", user.uid))
      .then(snap => setIsSystemAdmin(snap.exists()))
      .catch(() => setIsSystemAdmin(false));
  }, [user?.uid]);

  // Still loading auth
  if (authLoading || isSystemAdmin === null) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
      <div className="text-blue-400 text-xl">Loading…</div>
    </div>
  );

  // Not logged in
  if (!user) return <Navigate to="/login" replace />;

  // System admin → full system dashboard
  if (isSystemAdmin) return <AdminDashboard />;

  // Company owner or admin → company-scoped dashboard
  const role = userData?.role || "";
  if (role === "owner" || role === "admin") return <CompanyAdminDashboard />;

  // Everyone else → back to company dashboard
  return <Navigate to="/company" replace />;
};

/* ── App routes ─────────────────────────────────────────── */
const AppRoutes = () => (
  <Routes>
    {/* Public auth */}
    <Route path="/login"    element={<PublicRoute><LoginPage /></PublicRoute>} />
    <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

    {/* Public share — no auth required */}
    <Route path="/share"         element={<PublicSharePage />} />
    <Route path="/share/c/:slug" element={<PublicSharePage />} />

    {/* Company overview — main post-login landing */}
    <Route path="/company"
      element={<ProtectedRoute><CompanyDashboard /></ProtectedRoute>} />

    {/* User profile — display name + password */}
    <Route path="/profile"
      element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

    {/* Products / AI rules dashboard */}
    <Route path="/dashboard"
      element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

    {/* Campaigns */}
    <Route path="/campaigns"
      element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
    <Route path="/campaigns/:campaignId"
      element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />

    {/* Smart admin — auto-detects system admin vs company admin */}
    <Route path="/admin"
      element={<ProtectedRoute><AdminRoute /></ProtectedRoute>} />

    {/* Legacy sub-routes → redirect into unified admin with hash */}
    <Route path="/admin/features"
      element={<Navigate to="/admin#features" replace />} />
    <Route path="/admin/plans"
      element={<Navigate to="/admin#plans"    replace />} />

    {/* Root → login */}
    <Route path="/"  element={<Navigate to="/login" replace />} />
    <Route path="*"  element={<Navigate to="/login" replace />} />
  </Routes>
);

/* ── Global Footer — shows on every page ──────────────────── */
const GlobalFooter = () => {
  const { t, language } = useLanguage();
  return (
    <footer
      dir={language === "ar" ? "rtl" : "ltr"}
      className="w-full py-4 px-6 text-center text-xs text-gray-400 border-t border-gray-800 bg-gray-900/80 backdrop-blur-sm"
    >
      {t("footer_copyright")}
    </footer>
  );
};

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen flex flex-col">
            <div className="flex-1">
              <AppRoutes />
            </div>
            <GlobalFooter />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}